import { notification } from 'antd';
import config from 'config';
import { PaginatedParams } from 'core/http/pagination';
import { ListPayload, ListResponse, SuccessResponse } from 'core/http/response';
import axiosClient from '../../config/axios';
import { TemplateResponse } from './template';
import { ICS2MessageTypes, MessageTypes } from '../declarations/enums/common/declaration-types';
import { createIcs2EnsTemplate, getIcs2EnsTemplate, ics2EnsTemplatesUrl } from './ics2/client';
import { toIcs2EnsTemplate } from './ics2/ics2EnsTemplateMapper';
import { getTemplateEntityTypes } from './templateEntityTypes';

export interface ArchivedTemplatesParams {
    declarationExternalType: string;
    declarationInternalType: string;
    declarationName?: string;
    query?: string;
}

export const getTemplates = async (
    country: 'ireland' | 'uk' | 'eu',
    internalType: any,
    declarationType: MessageTypes,
    params?: Partial<PaginatedParams> & {
        query?: string;
    }
): Promise<ListPayload<TemplateResponse>> => {
    const finalParams = {
        ...params,
        declarationInternalType: internalType === 'IMPORT_NEW' ? 'IMPORT' : internalType,
        declarationName: declarationType,
        declarationExternalType:
            country === 'uk' ? (internalType === 'NCTS' ? 'HMRC' : 'CDS') : country === 'eu' ? 'ICS2' : 'REVENUE',
    };

    return axiosClient
        .get<ListResponse<TemplateResponse>>(`${config.declarationTemplatesUrl}`, { params: finalParams })
        .then((response) => response.data.payload);
};

export const getArchivedTemplates = (params: ArchivedTemplatesParams): Promise<ListPayload<TemplateResponse>> =>
    axiosClient
        .get<ListResponse<TemplateResponse>>(`${config.declarationTemplatesUrl}/archive`, { params })
        .then((response) => response.data.payload);

export const archiveTemplate = (declarationTemplateId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .put<SuccessResponse<void>>(`${config.declarationTemplatesUrl}/archive/${declarationTemplateId}`)
        .then((response) => response.data);
export const unarchiveTemplate = (declarationTemplateId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .put<SuccessResponse<void>>(`${config.declarationTemplatesUrl}/unarchive/${declarationTemplateId}`)
        .then((response) => response.data);

export const deleteTemplate = (declarationTemplateId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(`${config.declarationTemplatesUrl}/${declarationTemplateId}`)
        .then((response) => response.data);

export const createTemplate = (
    country: 'ireland' | 'uk' | 'eu',
    internalType: 'import' | 'export' | 'ens' | 'ncts',
    declarationType: MessageTypes,
    template: Partial<TemplateResponse>,
    teamId?: string
) => {
    const { declarationName, templateName, template: templateData } = template;

    if (country === 'eu') {
        // ICS2 ENS has its own endpoint and its own template shape: the generic endpoint binds the
        // template as a two-level pair, dropping the house levels and storing the payload untyped,
        // after which the ICS2 endpoints reject the row on every read.
        return axiosClient.post(ics2EnsTemplatesUrl, {
            declarationName,
            templateName,
            template: toIcs2EnsTemplate(templateData, declarationName as ICS2MessageTypes),
            teamId,
        });
    }

    const entityTypes = getTemplateEntityTypes(country, internalType);
    return axiosClient.post(
        config.declarationTemplatesUrl,
        { declarationName, templateName, template: templateData, teamId },
        { params: entityTypes }
    );
};

export const editTemplate = (template: Partial<TemplateResponse>) => {
    const { id, declarationName, templateName, template: templateData } = template;
    return axiosClient.put(`${config.declarationTemplatesUrl}/${id}`, {
        declarationName,
        templateName,
        template: templateData,
    });
};

export const duplicateTemplate = async (
    templateId: string,
    country: string,
    internalType: string,
    messageType: string
) => {
    try {
        // ICS2 ENS templates round-trip through their own endpoints: the generic create writes the
        // template untyped, which the ICS2 endpoints then refuse to read back.
        if (country === 'ICS2') {
            const source = await getIcs2EnsTemplate(templateId);
            return createIcs2EnsTemplate({
                declarationName: source.declarationName,
                templateName: source.templateName,
                template: source.template,
                teamId: source.teamId ?? undefined,
            });
        }

        // Partial<TemplateResponse>
        const res = await axiosClient.get<SuccessResponse<TemplateResponse>>(
            `${config.declarationTemplatesUrl}/${templateId}`
        );

        const _template = res.data.payload;
        const { id: _, ...template } = _template;
        const finalCountry =
            country === 'CDS' || country === 'HMRC'
                ? 'uk'
                : country === 'REVENUE'
                ? 'ireland'
                : country === 'ICS2'
                ? 'eu'
                : (country.toLowerCase() as 'ireland' | 'uk' | 'eu');
        const finalRes = await createTemplate(
            finalCountry,
            internalType.toLowerCase() as any,
            messageType as any,
            template
        );

        return finalRes.data.payload;
    } catch (err) {
        notification.error({ message: 'Could not duplicate the template, please try again later!', placement: 'top' });
    }
};
